// ===== 詳細 =====

import { Paper, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import StyledButton from "../../../components/StyledButton";
import { dataEntityText } from "../../../components/links/EntityLinks";
import { materialSearchUrl } from "../../../util/Materialperser";
import { userInfoCol } from "../../../util/userDisplay";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { carryInRow } from "../../../schema/console/carry_in";
import { LocationsById, MaterialsById, carryInStatusNode, isCarryInOver, lotGroups, resolvedMaterialCount, workLabel } from "./helpers";
import { CarryInLotTable } from "./CarryInLotTable";

export function CarryInDetailPage({ pageContext, locationsById, materialsById }: { pageContext: PageContext; locationsById: LocationsById; materialsById: MaterialsById }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<carryInRow>(() => {
    if (!id) {
      return Promise.resolve(null);
    }
    return api.carry_in.getFromId({ id });
  }, [api, id, retryCount]);

  return (
    <DetailGuard state={state} notFoundLabel={`搬入 ${id}`} onRetry={retry}>
      {(ci) => <CarryInDetailView ci={ci} navigate={navigate} locationsById={locationsById} materialsById={materialsById} />}
    </DetailGuard>
  );
}

function CarryInDetailView({ ci, navigate, locationsById, materialsById }: {
  ci: carryInRow; navigate: ReturnType<typeof useNavigate>; locationsById: LocationsById; materialsById: MaterialsById;
}) {
  const received = resolvedMaterialCount(ci);
  const complete = ci.require_material_count > 0 && received >= ci.require_material_count;
  const over = isCarryInOver(ci);
  // 超過は赤・未達も赤・予定ちょうどの完了のみ緑✓(出荷の「出荷個数超過」と同パターンの警告表示)。
  let receivedColor = "error.main";
  if (complete && !over) {
    receivedColor = "success.main";
  }
  const serials = ci.Materials.filter((m) => m.Lot != null).map((m) => m.id);
  const location = ci.carry_in_location_id != null ? locationsById.get(ci.carry_in_location_id) : undefined;
  // 搬入日時は完了時刻(carried_in_at)優先、未完了なら手入力の received_at を表示。
  const receivedAtDisplay = ci.carried_in_at ?? ci.received_at;

  const infoCols: InfoListColDef[] = [
    { label: "搬入ID", context: dataEntityText("carry-in", ci.id) },
    { label: "工事", context: ci.Work ? workLabel(ci.Work) : "-", targetUrl: ci.Work ? new URL(`/console/master-view/work/${ci.Work.id}/`, window.location.origin) : undefined },
    { label: "搬入場所", context: location?.name ?? "-", targetUrl: location ? new URL(`/console/master-view/carry-in-location/${location.id}/`, window.location.origin) : undefined },
    { label: "気温", context: ci.temperature != null ? `${ci.temperature}℃` : "-" },
    { label: "湿度", context: ci.humidity != null ? `${ci.humidity}%` : "-" },
    { label: "予定数", context: ci.require_material_count },
    { label: "搬入材料数", context: <Typography component="span" fontSize={20} color={receivedColor}>{complete && !over ? "✓ " : ""}{received}{over ? `(予定 ${ci.require_material_count} を超過)` : ""}</Typography>, targetUrl: serials.length > 0 ? new URL(materialSearchUrl(serials), window.location.origin) : undefined },
    { label: "搬入状況", context: carryInStatusNode(ci) },
    { label: "搬入日時", context: receivedAtDisplay != null ? new Time(receivedAtDisplay).Format("YYYY/MM/dd HH:mm:ss") : "-" },
    { label: "搬入者", context: ci.receiver_name ?? "-" },
    { label: "登録日", context: new Time(ci.createdAt).Format("YYYY/MM/dd HH:mm:ss") },
    userInfoCol(ci.User),
    { label: "備考", context: ci.remarks ?? "" },
  ];

  return (
    <Stack p={3} gap={3} maxWidth={1200} mx="auto">
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h4" fontWeight="bold">搬入情報</Typography>
        <StyledButton buttonStyle="primary" onClick={() => navigate("edit/")} sx={{ width: 120 }}>編集</StyledButton>
      </Stack>
      <InfoList cols={infoCols} columns={3} />
      {resolvedMaterialCount(ci) > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight="bold" mb={1}>含まれる材料ロット ({lotGroups(ci).length}件)</Typography>
          <CarryInLotTable ci={ci} materialsById={materialsById} />
        </Paper>
      )}
      <Stack alignItems="center">
        <StyledButton sx={{ py: 2, width: "60%" }} buttonStyle="primary" onClick={() => navigate("scan/")}>
          <Typography>搬入時QRコード読み取り</Typography>
        </StyledButton>
      </Stack>
    </Stack>
  );
}
